import axios from "axios";
import { apiOrigin as getApiOrigin, buildLoginUrl } from "../services/apiClient";

/**
 * Installs a global fetch interceptor so every `fetch(...)` call to the API
 * (all built by the service modules in src/services through apiClient.js)
 * gets Microsoft Entra ID session-cookie auth in one place:
 *
 * - Adds `credentials: "include"` to requests aimed at the API origin, so
 *   the backend's session cookie is actually sent cross-origin.
 * - On 401 (no session), redirects the browser to the backend's
 *   /auth/login, forwarding any login_hint this page itself received (e.g.
 *   from iai_portal's Launch link) so silent SSO can be attempted.
 * - On 403 (signed in, but missing App.ICMS/App.ADMIN), redirects to this
 *   app's own /forbidden route instead of looping back into login.
 *
 * Also sets axios.defaults.withCredentials = true globally - some
 * components (e.g. WarrentySubmitPage.jsx's AI extraction upload) use
 * axios directly rather than fetch(), and axios has its own separate
 * credentials setting that this fetch patch doesn't touch. Without this,
 * those requests silently went out with no session cookie at all and the
 * backend correctly - but confusingly - rejected them as unauthenticated
 * even for a signed-in user. This only adds the cookie; unlike the fetch
 * path above, it does not add 401/403 redirect handling for axios calls -
 * each of those still surfaces auth errors via its own catch block.
 *
 * Existing components' fetch()/axios calls, response handling, and
 * business logic are otherwise completely unchanged by this - the
 * interceptor only observes and augments requests/responses in flight.
 */
export function installAuthFetchInterceptor() {
  const apiOrigin = getApiOrigin();
  const nativeFetch = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    const url = typeof input === "string" ? input : input.url;
    const isApiCall = typeof url === "string" && url.startsWith(apiOrigin);

    const response = await nativeFetch(input, isApiCall ? { ...init, credentials: "include" } : init);

    if (isApiCall && response.status === 401) {
      window.location.href = buildLoginUrl(apiOrigin);
    } else if (isApiCall && response.status === 403) {
      window.location.href = "/forbidden";
    }

    return response;
  };

  axios.defaults.withCredentials = true;
}

// Kept as an export for existing importers; the implementation lives in services/apiClient.js.
export { buildLoginUrl };
